/**
 * Role sets and the resource→action→roles matrix, transcribed VERBATIM from
 * the old panel's `src/assets/data/roles.ts` + `src/assets/data/permissions.ts`.
 *
 * The old `CheckAccess` grants a `"resource.action"` string when the JWT role
 * is listed for that cell (action defaults to `"view"`), with a blanket bypass
 * for the main merchant account (see `lib/auth-claims.ts`). Every legacy screen
 * gate below replays those same strings, so this table is the one place the
 * matrix lives — same as the source.
 *
 * One deliberate exception: `reports.create` (the excel export on
 * `/delivery/reports`) is restricted to `REPORT_EXPORT_ROLES` — merchant-admin
 * only, manager and cashier denied — per the business rule, not the source.
 */
export const USER_TYPE_MERCHANT = 'merchant';

export const LEGACY_ROLES = {
  MERCHANT_ADMIN: 'merchant-admin',
  MANAGER: 'manager',
  CASHIER: 'cashier',
} as const;

// Legacy roles issued by the backend before the August 2026 role refactor.
// Older tokens still carry them, so they keep their pre-refactor access:
// VENDOR / VENDOR_ADMIN = admin, VENDOR_OPERATOR = write, VENDOR_OBSERVER = read-only.
export const LEGACY_BACKEND_ROLES = {
  VENDOR: 'VENDOR',
  VENDOR_ADMIN: 'VENDOR_ADMIN',
  VENDOR_OPERATOR: 'VENDOR_OPERATOR',
  VENDOR_OBSERVER: 'VENDOR_OBSERVER',
} as const;

export type LegacyAccessRole =
  | (typeof LEGACY_ROLES)[keyof typeof LEGACY_ROLES]
  | (typeof LEGACY_BACKEND_ROLES)[keyof typeof LEGACY_BACKEND_ROLES];

export const ALL_ROLES: LegacyAccessRole[] = [
  LEGACY_ROLES.MERCHANT_ADMIN,
  LEGACY_ROLES.MANAGER,
  LEGACY_ROLES.CASHIER,
  LEGACY_BACKEND_ROLES.VENDOR,
  LEGACY_BACKEND_ROLES.VENDOR_ADMIN,
  LEGACY_BACKEND_ROLES.VENDOR_OPERATOR,
  LEGACY_BACKEND_ROLES.VENDOR_OBSERVER,
];

// Roles allowed to create and modify entities (everything except read-only cashier).
export const WRITE_ROLES: LegacyAccessRole[] = [
  LEGACY_ROLES.MERCHANT_ADMIN,
  LEGACY_ROLES.MANAGER,
  LEGACY_BACKEND_ROLES.VENDOR,
  LEGACY_BACKEND_ROLES.VENDOR_ADMIN,
  LEGACY_BACKEND_ROLES.VENDOR_OPERATOR,
];

// Roles with full administrative access.
export const ADMIN_ROLES: LegacyAccessRole[] = [
  LEGACY_ROLES.MERCHANT_ADMIN,
  LEGACY_BACKEND_ROLES.VENDOR,
  LEGACY_BACKEND_ROLES.VENDOR_ADMIN,
];

// Roles allowed to generate and download report exports (the excel feature on
// `/delivery/reports`: the "New Report" button and each row's download).
// Business rule, NOT the old panel's verbatim matrix: only merchant-admin —
// manager and cashier are denied. The pre-refactor equivalents follow the
// mapping above: only VENDOR / VENDOR_ADMIN (= admin) keep access;
// VENDOR_OPERATOR (= write, like manager) and VENDOR_OBSERVER (= read-only,
// like cashier) do not.
export const REPORT_EXPORT_ROLES: LegacyAccessRole[] = [
  LEGACY_ROLES.MERCHANT_ADMIN,
  LEGACY_BACKEND_ROLES.VENDOR,
  LEGACY_BACKEND_ROLES.VENDOR_ADMIN,
];

export const legacyPermissions: Record<string, Record<string, readonly string[]>> = {
  delivery: {
    create: WRITE_ROLES,
    edit: WRITE_ROLES,
    reorder: WRITE_ROLES,
    cancel: WRITE_ROLES,
    view: ALL_ROLES,
  },
  profile: {
    view: ALL_ROLES,
  },
  staff: {
    view: WRITE_ROLES,
    create: ADMIN_ROLES,
    delete: ADMIN_ROLES,
  },
  addresses: {
    view: WRITE_ROLES,
    create: WRITE_ROLES,
    delete: WRITE_ROLES,
  },
  cards: {
    view: ALL_ROLES,
    create: ADMIN_ROLES,
    delete: ADMIN_ROLES,
    makeDefault: ADMIN_ROLES,
    topUp: ADMIN_ROLES,
  },
  clients: {
    view: ALL_ROLES,
  },
  reports: {
    view: ALL_ROLES,
    create: REPORT_EXPORT_ROLES,
  },
  integration: {
    view: ALL_ROLES,
    create: ADMIN_ROLES,
    delete: ADMIN_ROLES,
  },
  chats: {
    view: WRITE_ROLES,
  },
};

/**
 * Every `"resource.action"` string the matrix answers. The lookup itself still
 * accepts any string (an unknown cell simply denies, as the source does) —
 * this union is only what call sites should pass.
 */
export type LegacyPermission =
  | 'delivery.create'
  | 'delivery.edit'
  | 'delivery.reorder'
  | 'delivery.cancel'
  | 'delivery.view'
  | 'delivery'
  | 'profile.view'
  | 'profile'
  | 'staff.view'
  | 'staff.create'
  | 'staff.delete'
  | 'addresses.view'
  | 'addresses.create'
  | 'addresses.delete'
  | 'addresses'
  | 'cards.view'
  | 'cards.create'
  | 'cards.delete'
  | 'cards.makeDefault'
  | 'cards.topUp'
  | 'cards'
  | 'clients.view'
  | 'clients'
  | 'reports.view'
  | 'reports.create'
  | 'reports'
  | 'integration.view'
  | 'integration.create'
  | 'integration.delete'
  | 'integration'
  | 'chats.view'
  | 'chats';
